import React, { useState } from 'react';
import {
    backgroundFlexibleProp,
    backgroundTypeProp,
    mediaQueryProp,
    videoProp,
} from 'utils/proptypes/modules/mediaProps';

import AspectWrapper from 'components/wrappers/AspectWrapper';
import Content from 'components/contentBlocks/VideoPlayer/Content';
import FlexibleWrapper from 'components/wrappers/FlexibleWrapper';
import PropTypes from 'prop-types';
import Video from 'components/base/video/Video';
import VideoPlayButton from 'components/buttons/styledButtons/VideoPlayButton';
import { headingTagProp } from 'utils/proptypes/modules/textProps';
import { mediaQueries } from 'config/theme/breakpoints';
import ratios from 'config/theme/ratios';
import styled from 'libs/styled';

const Wrapper = styled('div')`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;

    video:focus {
        outline: none;
    }
`;

/**
 * A video block that will by displayed like a FlexibleBlock with a playbutton.
 * When the user presses the playbutton the video supplied to video-prop will be displayed instead.
 *
 * @param {object} background - A video/image object. This will be displayed before the play button is pressed.
 * @param {number | number[number]} backgroundRatio - Ratio of background/video.
 * @param {string} backgroundType - The type of background that should be used; video or image.
 * @param {string} buttonText - Text in play button.
 * @param {string} heading - Heading as a string.
 * @param {string} headingTag - The html-tag used by heading; h1, h2, h3 etc.
 * @param {string} invertedPlayButtonColor - Inverted color of buttonText, used for hover effect.
 * @param {array} mediaQuery - Used if background is a image, can also be a part of the background object.
 * @param {string} playButtonColor - Color of buttonText.
 * @param {string} tag - Small tag displayed below heading.
 * @param {string} textColor - Color of text.
 * @param {object} video - A video object. This is the video that will start when the user presses play.
 */

const VideoPlayer = ({
    background,
    backgroundRatio = [ratios.vertical, ratios.square, ratios.horizontal],
    backgroundType,
    buttonText = '',
    heading = '',
    headingTag = 'h2',
    invertedPlayButtonColor = '',
    mediaQuery = mediaQueries.fullWidth,
    playButtonColor = '',
    tag = '',
    textColor = '',
    video,
}) => {
    const { autoplay, controls, loop, muted, poster, url } = video;
    const [showVideo, setShowVideo] = useState(false);

    // When the user has pressed play
    return showVideo ? (
        <Wrapper>
            <AspectWrapper ratio={backgroundRatio}>
                <Video
                    autoPlay={autoplay}
                    controls={controls}
                    loop={loop}
                    muted={muted}
                    playsInline={autoplay}
                    poster={poster && poster.url}
                    src={url}
                />
            </AspectWrapper>
            <Content heading={heading} headingTag={headingTag} tag={tag} />
        </Wrapper>
    ) : (
        // Before the user has pressed play
        <Wrapper color={textColor}>
            <AspectWrapper ratio={backgroundRatio}>
                <FlexibleWrapper background={background} backgroundType={backgroundType} mediaQuery={mediaQuery}>
                    <button type="button" onClick={() => setShowVideo(true)}>
                        <VideoPlayButton color={playButtonColor} hoverPlayButtonColor={invertedPlayButtonColor}>
                            {buttonText}
                        </VideoPlayButton>
                    </button>
                </FlexibleWrapper>
            </AspectWrapper>
            <Content heading={heading} headingTag={headingTag} tag={tag} />
        </Wrapper>
    );
};

VideoPlayer.propTypes = {
    background: backgroundFlexibleProp.isRequired,
    backgroundRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
    backgroundType: backgroundTypeProp.isRequired,
    buttonText: PropTypes.string,
    heading: PropTypes.string,
    headingTag: headingTagProp,
    invertedPlayButtonColor: PropTypes.string,
    mediaQuery: mediaQueryProp,
    playButtonColor: PropTypes.string,
    tag: PropTypes.string,
    textColor: PropTypes.string,
    video: videoProp.isRequired,
};

export default VideoPlayer;
